import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

function getCatName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => article.relatedSlugs.includes(a.slug));
  const catName = getCatName(article.category);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.content.replace(/<[^>]*>/g, '').slice(0, 160),
            datePublished: article.date,
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: '福利视频导航', url: 'https://fulishipin.com' },
            url: `https://fulishipin.com/article/${slug}`,
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://fulishipin.com/article/${slug}` },
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                >
                  {catName}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">{tag}</span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-p:mb-4 prose-p:leading-relaxed
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block p-4 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-background-50 transition-all"
                  >
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1">{rel.title}</h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{rel.content.replace(/<[^>]*>/g, '').slice(0, 120)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] text-foreground-400">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}